import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSponsorSlot,
  deleteSponsorSlot,
  emailSponsorReport,
  getGoalSponsorReport,
  getSponsorReport,
  listRounds,
  listSponsorSlots,
  printSponsorReport,
} from '../api/portal';
import { ApiError } from '../api/client';

function thisMonth() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function SponsorPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth);
  const report = useQuery({
    queryKey: ['portal-sponsor', month],
    queryFn: () => getSponsorReport(month),
  });
  const slots = useQuery({ queryKey: ['portal-sponsor-slots'], queryFn: listSponsorSlots });
  const rounds = useQuery({ queryKey: ['portal-rounds'], queryFn: listRounds });
  const r = report.data;
  const [err, setErr] = useState<string | null>(null);
  const [roundKey, setRoundKey] = useState('');
  const [label, setLabel] = useState('');
  const [logo, setLogo] = useState('');
  const [notes, setNotes] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [goalSponsor, setGoalSponsor] = useState(true);
  const [goalReport, setGoalReport] = useState<string | null>(null);
  const [mailTo, setMailTo] = useState('');
  const hint = useMemo(() => (
    'Rasprodaja slota: rezerviši logo na live-score/tabeli za kolo. PDF broji mount/view — dokaz je prilog.'
  ), []);
  const addSlot = useMutation({
    mutationFn: () => createSponsorSlot({
      round_key: roundKey,
      sponsor_label: label.trim() || undefined,
      logo_url: logo.trim(),
      notes: notes.trim() || undefined,
      report_email: reportEmail.trim() || undefined,
      goal_sponsor: goalSponsor,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-sponsor-slots'] });
      qc.invalidateQueries({ queryKey: ['portal-sponsor'] });
      setLogo('');
      setNotes('');
      setErr(null);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  const removeSlot = useMutation({
    mutationFn: deleteSponsorSlot,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-sponsor-slots'] }),
  });

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Liga</div>
        <h1 className="page-title">Sponzor inventory</h1>
        <p className="page-sub">{hint} Ako je uključen sponzor gola, isto ime ide na svaki gol tog kola: On-Air, goal-bug i Wire.</p>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label>
          <span className="label">Mesec</span>
          <input className="input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <button
          type="button"
          className="btn-primary"
          onClick={() => printSponsorReport(month).catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))}
        >
          Print / PDF
        </button>
        <input className="input w-56" type="email" value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="mejl sponzora" />
        <button
          type="button"
          className="btn-secondary"
          onClick={() => emailSponsorReport(month, mailTo || undefined)
            .then((res) => setErr(`Poslato na ${res.to}`))
            .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))}
        >
          Pošalji mejlom
        </button>
      </div>
      {err && <div className="alert-bad mb-3">{err}</div>}
      <form
        className="card mb-4 grid gap-3 p-4 md:grid-cols-5"
        onSubmit={(e: FormEvent) => { e.preventDefault(); addSlot.mutate(); }}
      >
        <div>
          <label className="label">Kolo</label>
          <select className="input" value={roundKey} onChange={(e) => setRoundKey(e.target.value)} required>
            <option value="">Izaberi kolo</option>
            {(rounds.data ?? []).map((x) => (
              <option key={x.key} value={x.key}>{x.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Sponzor</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Arena" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Logo URL</label>
          <input className="input" value={logo} onChange={(e) => setLogo(e.target.value)} required placeholder="https://…" />
        </div>
        <div className="flex items-end">
          <button className="btn-primary" disabled={addSlot.isPending || !roundKey || !logo.trim()}>Rezerviši slot</button>
        </div>
        <div className="md:col-span-3">
          <label className="label">Napomena</label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Kolo 8–12, LED + tabela" />
        </div>
        <label className="md:col-span-5 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={goalSponsor} onChange={(e) => setGoalSponsor(e.target.checked)} />
          Sponzor gola — ime na svakom golu ovog kola (On-Air, goal-bug, Wire)
        </label>
        <div className="md:col-span-2">
          <label className="label">Mejl brendu (mesečni PDF)</label>
          <input className="input" type="email" value={reportEmail} onChange={(e) => setReportEmail(e.target.value)} placeholder="brand@arena.rs" />
        </div>
      </form>
      <div className="card mb-4 p-4">
        <div className="mb-2 text-[11px] uppercase text-slate-500">Rezervisani slotovi</div>
        {(slots.data ?? []).map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 py-1 text-sm">
            <span className="text-white">
              {s.round_key} · {s.sponsor_label}
              {s.goal_sponsor && <span className="ml-2 text-xs text-amber-200">sponzor gola</span>}
            </span>
            <span className="flex gap-2">
              {s.goal_sponsor && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => getGoalSponsorReport(s.round_key)
                    .then((rep) => setGoalReport(`${rep.round_key}: ${rep.goals} golova · ${rep.sponsor_label || '—'} · ${rep.surfaces.join(', ')}`))
                    .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))}
                >
                  Golovi
                </button>
              )}
              <button type="button" className="btn-ghost text-xs" onClick={() => removeSlot.mutate(s.id)}>Ukloni</button>
            </span>
          </div>
        ))}
        {(slots.data ?? []).length === 0 && <div className="text-sm text-slate-500">Još nema rezervacije. Default logo ostaje sa Usage.</div>}
        {goalReport && <div className="mt-2 text-sm text-amber-100">{goalReport}</div>}
      </div>
      {r && (
        <>
          <div className="grid gap-3 md:grid-cols-3 mb-4">
            <Stat label="Montaže" value={String(r.mounts)} />
            <Stat label="Pregledi" value={String(r.views)} />
            <Stat label="Sponzor" value={r.sponsor_label || '—'} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Table title="Po klubu" rows={(r.by_club ?? []).map((x) => [x.name, x.count])} />
            <Table title="Po kolu" rows={(r.by_round ?? []).map((x) => [x.name, x.count])} />
          </div>
          <div className="card mt-4 p-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Po widgetu</div>
            {(r.by_widget ?? []).map((x) => (
              <div key={`${x.widget}-${x.event}`} className="flex justify-between text-sm py-0.5">
                <span className="text-slate-300">{x.widget} · {x.event}</span>
                <span className="tabular-nums text-white">{x.count}</span>
              </div>
            ))}
            {(r.by_widget ?? []).length === 0 && <div className="text-sm text-slate-500">Nema utisaka ovog meseca.</div>}
          </div>
          {(r.slots ?? []).length > 0 && (
            <div className="card mt-4 p-4">
              <div className="text-[11px] uppercase text-slate-500 mb-2">Slotovi u PDF-u</div>
              {r.slots!.map((s) => (
                <div key={s.round_key} className="flex justify-between text-sm py-0.5">
                  <span className="text-slate-300">{s.round_key} · {s.sponsor_label}</span>
                  <span className="truncate text-xs text-slate-500">{s.logo_url}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3">
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

function Table({ title, rows }: { title: string; rows: [string, number][] }) {
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase text-slate-500 mb-2">{title}</div>
      {rows.map(([n, c]) => (
        <div key={n} className="flex justify-between text-sm py-0.5">
          <span className="text-slate-300">{n}</span>
          <span className="tabular-nums text-white">{c}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="text-sm text-slate-500">—</div>}
    </div>
  );
}
